import { useMutation, useQueryClient } from '@tanstack/react-query';
import { labelQueueApi } from '../lib/labelQueueApi';
import { useToast } from './useToast';

export function useAddToLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (plantId: string) => labelQueueApi.addPlantToQueue(plantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Added to queue',
        description: 'Plant added to label queue successfully',
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      // Check if this is a QR code missing error
      const apiError = error as { response?: { data?: { error?: string } }; message?: string };
      if (apiError.response?.data?.error?.includes('does not have a QR code')) {
        showToast({
          title: 'QR Code Required',
          description: 'Please generate a QR code for this plant first',
          variant: 'info',
          action: {
            label: 'Generate QR',
            onClick: () => {
              // This will be handled by the component that calls this mutation
            },
          },
        });
      } else {
        showToast({
          title: 'Error',
          description: apiError.message || 'Failed to add plant to queue',
          variant: 'error',
        });
      }
    },
  });
}

export function useAddMultipleToLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (plantIds: string[]) => labelQueueApi.addMultiplePlantsToQueue(plantIds),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Added to queue',
        description: `${data.length} plants added to label queue`,
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      // Check if this is a QR code missing error
      const apiError = error as {
        response?: {
          data?: { error?: string; data?: { plantsWithoutQR?: { displayName: string }[] } };
        };
        message?: string;
      };
      if (apiError.response?.data?.error?.includes('do not have QR codes')) {
        const plantsWithoutQR = apiError.response?.data?.data?.plantsWithoutQR || [];
        const plantNames = plantsWithoutQR.map(p => p.displayName).join(', ');
        showToast({
          title: 'QR Codes Required',
          description: `The following plants need QR codes: ${plantNames}`,
          variant: 'info',
          duration: 5000,
        });
      } else {
        showToast({
          title: 'Error',
          description: apiError.message || 'Failed to add plants to queue',
          variant: 'error',
        });
      }
    },
  });
}

export function useRemoveFromLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (plantId: string) => labelQueueApi.removePlantFromQueue(plantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Removed from queue',
        description: 'Plant removed from label queue',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        description: error.message || 'Failed to remove plant from queue',
        variant: 'error',
      });
    },
  });
}

export function useClearLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: () => labelQueueApi.clearQueue(),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Queue cleared',
        description: `${data.deleted} items removed from label queue`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        description: error.message || 'Failed to clear queue',
        variant: 'error',
      });
    },
  });
}

// Seed-specific mutations
export function useAddSeedToLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (seedBatchId: string) => labelQueueApi.addSeedToQueue(seedBatchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Added to queue',
        description: 'Seed batch added to label queue successfully',
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } }; message?: string };
      if (apiError.response?.data?.error?.includes('does not have a QR code')) {
        showToast({
          title: 'QR Code Required',
          description: 'Please generate a QR code for this seed batch first',
          variant: 'info',
          action: {
            label: 'Generate QR',
            onClick: () => {
              // This will be handled by the component that calls this mutation
            },
          },
        });
      } else {
        showToast({
          title: 'Error',
          description: apiError.message || 'Failed to add seed batch to queue',
          variant: 'error',
        });
      }
    },
  });
}

export function useAddMultipleSeedsToLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (seedBatchIds: string[]) => labelQueueApi.addMultipleSeedsToQueue(seedBatchIds),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Added to queue',
        description: `${data.length} seed batches added to label queue`,
        variant: 'success',
      });
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: {
          data?: { error?: string; data?: { seedsWithoutQR?: { displayName: string }[] } };
        };
        message?: string;
      };
      if (apiError.response?.data?.error?.includes('do not have QR codes')) {
        const seedsWithoutQR = apiError.response?.data?.data?.seedsWithoutQR || [];
        const seedNames = seedsWithoutQR.map(s => s.displayName).join(', ');
        showToast({
          title: 'QR Codes Required',
          description: `The following seed batches need QR codes: ${seedNames}`,
          variant: 'info',
          duration: 5000,
        });
      } else {
        showToast({
          title: 'Error',
          description: apiError.message || 'Failed to add seed batches to queue',
          variant: 'error',
        });
      }
    },
  });
}

export function useRemoveSeedFromLabelQueue() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (seedBatchId: string) => labelQueueApi.removeSeedFromQueue(seedBatchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labelQueue'] });
      queryClient.invalidateQueries({ queryKey: ['labelQueueCount'] });
      showToast({
        title: 'Removed from queue',
        description: 'Seed batch removed from label queue',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        description: error.message || 'Failed to remove seed batch from queue',
        variant: 'error',
      });
    },
  });
}
